import { CaretDown, CaretUp } from '@liveauctioneers/hammer-ui-core/icons';
import { FormattedMessage } from '@liveauctioneers/hammer-ui-core/intl';
import { H2 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { debounce } from 'es-toolkit';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { Collapse } from 'react-collapse';

import Pagination from '@/components/Pagination/hammer-pagination/Pagination';
import {
    defaultOtherCategoriesOptions,
    defaultOtherCategoriesPageSize,
} from '@/pages/DiscoverCategories/components/DiscoverCategories/components/OtherCategories/constants/otherCategoriesConstants';
import { OtherCategoryCard } from '@/pages/DiscoverCategories/components/DiscoverCategories/components/OtherCategories/OtherCategoryCard/OtherCategoryCard';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { fetchOtherCategoriesIfNeeded } from '@/redux/modules/otherCategories/otherCategories';
import {
    createOtherCategoriesByPageSelector,
    otherCategoriesTotalResultsSelector,
} from '@/redux/modules/otherCategories/otherCategories.selectors';

export const OtherCategoriesDropDown = (): ReactElement => {
    const dispatch = useAppDispatch();
    const titleRef = useRef<HTMLDivElement>(null);
    const [isOpen, setIsOpen] = useState(false);
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(defaultOtherCategoriesPageSize);
    const [currentCategories, setCurrentCategories] = useState([]);
    const categories = useAppSelector(createOtherCategoriesByPageSelector(pageNumber, pageSize));
    const totalResults = useAppSelector(otherCategoriesTotalResultsSelector);

    useEffect(() => {
        dispatch(fetchOtherCategoriesIfNeeded(pageNumber, pageSize));
    }, [dispatch, pageNumber, pageSize]);

    useEffect(() => {
        if (categories) {
            setCurrentCategories(categories);
        }
    }, [categories]);

    const onPageChange = ({ page: newPageNumber, pageSize: newPageSize }) => {
        setPageNumber(newPageNumber);
        if (newPageSize) {
            setPageSize(newPageSize);
        }
        if (titleRef?.current?.scrollIntoView) {
            titleRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    const toggleOpen = () => {
        setIsOpen(!isOpen);
    };

    return (
        <section>
            <div
                className="flex flex-row justify-between"
                onClick={toggleOpen}
            >
                <H2
                    className="my-md"
                    ref={titleRef}
                >
                    <FormattedMessage id="discoverPage.otherCategories.title" />
                </H2>
                <div className="flex items-center">
                    {isOpen ? (
                        <CaretUp
                            data-testid="up-arrow"
                            size="xs"
                            style="fill"
                        />
                    ) : (
                        <CaretDown
                            data-testid="down-arrow"
                            size="xs"
                            style="fill"
                        />
                    )}
                </div>
            </div>
            <Collapse isOpened={isOpen}>
                <div className="[&_[class^='paginationWrapper']]:mx-auto [&_[class^='paginationWrapper']]:w-1/2 [&_[class^='paginator']]:mt-lg">
                    <Pagination
                        onChange={debounce(onPageChange, 200)}
                        pageSizeOptions={defaultOtherCategoriesOptions}
                        paginationFilter={{
                            page: pageNumber,
                            pageSize,
                            totalRecords: totalResults,
                        }}
                    >
                        <div className="flex flex-wrap justify-between">
                            {currentCategories.map((category) => (
                                <OtherCategoryCard
                                    category={category}
                                    key={category.title}
                                />
                            ))}
                        </div>
                    </Pagination>
                </div>
            </Collapse>
            <div className={cn('border border-border-secondary', isOpen && 'mt-lg')} />
        </section>
    );
};
